import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Form, Row, Col, Alert, Spinner, Modal, Pagination } from 'react-bootstrap';
import { apiService } from '../../services/api';

interface BlacklistVehicle {
  id: string;
  plateNumber: string;
  ownerName: string;
  ownerPhone: string;
  ownerAddress?: string;
  vehicleType: 'car' | 'motorcycle' | 'truck';
  brand: string;
  model: string;
  color: string;
  registrationDate: string;
  blacklistDate: string;
  blacklistReason: string;
  blacklistBy: string;
  status: 'blacklisted' | 'inactive';
  notes?: string;
  totalViolations: number;
  lastViolation?: string;
}

interface FormData {
  plateNumber: string;
  ownerName: string;
  ownerPhone: string;
  ownerAddress: string;
  vehicleType: 'car' | 'motorcycle' | 'truck';
  brand: string;
  model: string;
  color: string;
  blacklistReason: string;
  notes: string;
}

interface SearchFilters {
  plateNumber: string;
  ownerName: string;
  ownerPhone: string;
  brand: string;
  color: string;
  vehicleType: 'all' | 'car' | 'motorcycle' | 'truck';
  blacklistReason: string;
  blacklistBy: string;
  blacklistDateFrom: string;
  blacklistDateTo: string;
  status: 'all' | 'blacklisted' | 'inactive';
}

const BlacklistManagement: React.FC = () => {
  const [blacklistVehicles, setBlacklistVehicles] = useState<BlacklistVehicle[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<BlacklistVehicle | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchPlate, setSearchPlate] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState<string>('all');
  const vehiclesPerPage = 10;
  
  // 增強搜尋篩選狀態
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({
    plateNumber: '',
    ownerName: '',
    ownerPhone: '',
    brand: '',
    color: '',
    vehicleType: 'all',
    blacklistReason: '',
    blacklistBy: '',
    blacklistDateFrom: '',
    blacklistDateTo: '',
    status: 'all'
  });
  
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);

  // 表單狀態
  const [formData, setFormData] = useState<FormData>({
    plateNumber: '',
    ownerName: '',
    ownerPhone: '',
    ownerAddress: '',
    vehicleType: 'car',
    brand: '',
    model: '',
    color: '',
    blacklistReason: '',
    notes: ''
  });

  // 載入黑名單車輛數據
  const loadBlacklistVehicles = async () => {
    try {
      setLoading(true);
      setError('');
      
      // 模擬API調用 - 實際應該調用專門的黑名單API
      const response = await apiService.getVehicles({
        page: currentPage,
        limit: vehiclesPerPage,
        search: searchPlate,
        status: 'blacklisted', // 只獲取黑名單車輛
        type: vehicleTypeFilter !== 'all' ? vehicleTypeFilter : undefined
      });
      
      if (response.success && response.data) {
        // 轉換API數據格式到前端格式
        const formattedVehicles: BlacklistVehicle[] = response.data.items?.map((vehicle: any) => ({
          id: vehicle._id || vehicle.id,
          plateNumber: vehicle.licensePlate || vehicle.plateNumber,
          ownerName: vehicle.owner?.name || vehicle.ownerName || '未知',
          ownerPhone: vehicle.owner?.phone || vehicle.ownerPhone || '未提供',
          ownerAddress: vehicle.owner?.address || vehicle.ownerAddress || '',
          vehicleType: vehicle.type || vehicle.vehicleType || 'car',
          brand: vehicle.brand || '未知',
          model: vehicle.model || '未知',
          color: vehicle.color || '未知',
          registrationDate: vehicle.createdAt ? new Date(vehicle.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          blacklistDate: vehicle.blacklistDate ? new Date(vehicle.blacklistDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          blacklistReason: vehicle.blacklistReason || '違規停車',
          blacklistBy: vehicle.blacklistBy || '系統管理員',
          status: vehicle.status === 'inactive' ? 'inactive' : 'blacklisted',
          notes: vehicle.notes || '',
          totalViolations: vehicle.totalViolations || 0,
          lastViolation: vehicle.lastViolation ? new Date(vehicle.lastViolation).toLocaleString() : undefined
        })) || [];
        
        setBlacklistVehicles(formattedVehicles);
      } else {
        setError('載入黑名單數據失敗');
      }
    } catch (error: any) {
      console.error('載入黑名單失敗:', error);
      setError('載入黑名單數據失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBlacklistVehicles();
  }, []);

  const getVehicleTypeText = (type: string) => {
    switch (type) {
      case 'car': return '汽車';
      case 'motorcycle': return '機車';
      case 'truck': return '卡車';
      default: return '未知';
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'blacklisted' ? 'danger' : 'secondary';
  };

  const getStatusText = (status: string) => {
    return status === 'blacklisted' ? '生效中' : '已停用';
  };

  // 過濾黑名單車輛 - 增強版搜尋邏輯
  const filteredVehicles = blacklistVehicles.filter(vehicle => {
    // 基本搜尋
    const matchesPlate = vehicle.plateNumber.toLowerCase().includes(searchPlate.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && vehicle.status === 'blacklisted') || 
      (statusFilter === 'inactive' && vehicle.status === 'inactive');
    const matchesType = vehicleTypeFilter === 'all' || vehicle.vehicleType === vehicleTypeFilter;
    
    // 進階搜尋
    const matchesAdvancedPlate = vehicle.plateNumber.toLowerCase().includes(searchFilters.plateNumber.toLowerCase());
    const matchesOwnerName = vehicle.ownerName.toLowerCase().includes(searchFilters.ownerName.toLowerCase());
    const matchesOwnerPhone = vehicle.ownerPhone.includes(searchFilters.ownerPhone);
    const matchesBrand = vehicle.brand.toLowerCase().includes(searchFilters.brand.toLowerCase());
    const matchesColor = vehicle.color.toLowerCase().includes(searchFilters.color.toLowerCase());
    const matchesAdvancedType = searchFilters.vehicleType === 'all' || vehicle.vehicleType === searchFilters.vehicleType;
    const matchesReason = vehicle.blacklistReason.toLowerCase().includes(searchFilters.blacklistReason.toLowerCase());
    const matchesAdvancedStatus = searchFilters.status === 'all' || vehicle.status === searchFilters.status;
    
    // 日期篩選
    let matchesBlacklistDate = true;
    if (searchFilters.blacklistDateFrom) {
      matchesBlacklistDate = matchesBlacklistDate && new Date(vehicle.blacklistDate) >= new Date(searchFilters.blacklistDateFrom);
    }
    if (searchFilters.blacklistDateTo) {
      matchesBlacklistDate = matchesBlacklistDate && new Date(vehicle.blacklistDate) <= new Date(searchFilters.blacklistDateTo);
    }
    
    return matchesPlate && matchesStatus && matchesType && 
           matchesAdvancedPlate && matchesOwnerName && matchesOwnerPhone && 
           matchesBrand && matchesColor && matchesAdvancedType && 
           matchesReason && matchesAdvancedStatus && matchesBlacklistDate;
  });

  // 分頁
  const indexOfLastVehicle = currentPage * vehiclesPerPage;
  const indexOfFirstVehicle = indexOfLastVehicle - vehiclesPerPage;
  const currentVehicles = filteredVehicles.slice(indexOfFirstVehicle, indexOfLastVehicle);
  const totalPages = Math.ceil(filteredVehicles.length / vehiclesPerPage);

  const handleAddToBlacklist = () => {
    setSelectedVehicle(null);
    setIsEditing(false);
    setFormData({
      plateNumber: '',
      ownerName: '',
      ownerPhone: '',
      ownerAddress: '',
      vehicleType: 'car',
      brand: '',
      model: '',
      color: '',
      blacklistReason: '',
      notes: ''
    });
    setShowModal(true);
  };

  // 重置搜尋篩選
  const resetFilters = () => {
    setSearchPlate('');
    setStatusFilter('all');
    setVehicleTypeFilter('all');
    setSearchFilters({
      plateNumber: '',
      ownerName: '',
      ownerPhone: '',
      brand: '',
      color: '',
      vehicleType: 'all',
      blacklistReason: '',
      blacklistBy: '',
      blacklistDateFrom: '',
      blacklistDateTo: '',
      status: 'all'
    });
    setCurrentPage(1);
  };

  // 更新搜尋篩選
  const updateSearchFilter = (field: keyof SearchFilters, value: string) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1);
  };

  const handleEditBlacklistVehicle = (vehicle: BlacklistVehicle) => {
    setSelectedVehicle(vehicle);
    setIsEditing(true);
    setFormData({
      plateNumber: vehicle.plateNumber,
      ownerName: vehicle.ownerName,
      ownerPhone: vehicle.ownerPhone,
      ownerAddress: vehicle.ownerAddress || '',
      vehicleType: vehicle.vehicleType,
      brand: vehicle.brand,
      model: vehicle.model,
      color: vehicle.color,
      blacklistReason: vehicle.blacklistReason,
      notes: vehicle.notes || ''
    });
    setShowModal(true);
  };

  const handleSaveBlacklistVehicle = async () => {
    try {
      // 驗證必填欄位
      if (!formData.plateNumber || !formData.ownerName || !formData.ownerPhone || !formData.blacklistReason) {
        alert('請填寫所有必填欄位');
        return;
      }

      // 構建發送到後端的數據格式
      const vehicleData = {
        licensePlate: formData.plateNumber,
        brand: formData.brand,
        model: formData.model,
        color: formData.color,
        ownerName: formData.ownerName,
        ownerPhone: formData.ownerPhone,
        ownerEmail: '', // 可選字段，設為空字符串
        ownerAddress: formData.ownerAddress,
        isVip: false,
        status: 'blacklisted' as const, // 設置為黑名單狀態
        blacklistReason: formData.blacklistReason,
        blacklistDate: new Date().toISOString(),
        blacklistBy: '系統管理員', // 實際應該從用戶會話中獲取
        notes: formData.notes
      };

      if (isEditing && selectedVehicle) {
        // 更新黑名單車輛
        await apiService.updateVehicle(selectedVehicle.id, vehicleData);
        alert('黑名單車輛更新成功！');
      } else {
        // 新增黑名單車輛
        await apiService.createVehicle(vehicleData);
        alert('車輛已加入黑名單！');
      }
      
      // 重新載入黑名單列表
      await loadBlacklistVehicles();
      
      // 關閉模態框並重置表單
      setShowModal(false);
      setFormData({
        plateNumber: '',
        ownerName: '',
        ownerPhone: '',
        ownerAddress: '',
        vehicleType: 'car',
        brand: '',
        model: '',
        color: '',
        blacklistReason: '',
        notes: ''
      });
      setSelectedVehicle(null);
      setIsEditing(false);
    } catch (error: any) {
      console.error('保存黑名單車輛失敗:', error);
      alert(`保存失敗：${error.response?.data?.message || error.message}`);
    }
  };

  const handleRemoveFromBlacklist = async (vehicleId: string) => {
    if (window.confirm('確定要將此車輛從黑名單中移除嗎？')) {
      try {
        // 更新車輛狀態為正常
        await apiService.updateVehicle(vehicleId, { status: 'active' });
        alert('車輛已從黑名單中移除！');
        // 重新載入黑名單列表
        await loadBlacklistVehicles();
      } catch (error: any) {
        console.error('移除黑名單失敗:', error);
        alert(`移除失敗：${error.response?.data?.message || error.message}`);
      }
    }
  };

  const handleToggleBlacklistStatus = async (vehicleId: string, currentStatus: string) => {
    const action = currentStatus === 'blacklisted' ? '停用' : '啟用';
    if (window.confirm(`確定要${action}此黑名單記錄嗎？`)) {
      try {
        const newStatus = currentStatus === 'blacklisted' ? 'inactive' : 'blacklisted';
        await apiService.updateVehicle(vehicleId, { status: newStatus });
        alert(`黑名單記錄已${action}！`);
        // 重新載入黑名單列表
        await loadBlacklistVehicles();
      } catch (error: any) {
        console.error(`${action}黑名單失敗:`, error);
        alert(`${action}失敗：${error.response?.data?.message || error.message}`);
      }
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">載入中...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="text-center">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {error}
      </Alert>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>
          <i className="fas fa-ban me-3"></i>
          黑名單管理
        </h1>
        <div>
          <Button variant="danger" onClick={handleAddToBlacklist} className="me-2">
            <i className="fas fa-plus me-2"></i>
            加入黑名單
          </Button>
          <Button variant="primary" onClick={loadBlacklistVehicles}>
            <i className="fas fa-sync-alt me-2"></i>
            重新整理
          </Button>
        </div>
      </div>

      {/* 搜尋和過濾 */}
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="fas fa-search me-2"></i>
            搜尋與篩選
          </h5>
          <div>
            <Button 
              variant="outline-primary" 
              size="sm" 
              onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
              className="me-2"
            >
              <i className={`fas fa-${showAdvancedSearch ? 'minus' : 'plus'} me-1`}></i>
              {showAdvancedSearch ? '收起進階搜尋' : '展開進階搜尋'}
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={resetFilters}>
              <i className="fas fa-undo me-1"></i>
              重置
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {/* 基本搜尋 */}
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>快速搜尋車牌號碼</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="請輸入車牌號碼"
                  value={searchPlate}
                  onChange={(e) => setSearchPlate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>狀態篩選</Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">全部狀態</option>
                  <option value="active">生效中</option>
                  <option value="inactive">已停用</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>車輛類型</Form.Label>
                <Form.Select
                  value={vehicleTypeFilter}
                  onChange={(e) => setVehicleTypeFilter(e.target.value)}
                >
                  <option value="all">全部類型</option>
                  <option value="car">汽車</option>
                  <option value="motorcycle">機車</option>
                  <option value="truck">卡車</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* 進階搜尋 */}
          {showAdvancedSearch && (
            <>
              <hr />
              <h6 className="text-primary mb-3">
                <i className="fas fa-filter me-2"></i>
                進階搜尋選項
              </h6>
              
              {/* 第一行 - 基本資訊搜尋 */}
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>車牌號碼</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="車牌號碼"
                      value={searchFilters.plateNumber}
                      onChange={(e) => updateSearchFilter('plateNumber', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>車主姓名</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="車主姓名"
                      value={searchFilters.ownerName}
                      onChange={(e) => updateSearchFilter('ownerName', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>聯絡電話</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="聯絡電話"
                      value={searchFilters.ownerPhone}
                      onChange={(e) => updateSearchFilter('ownerPhone', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>黑名單原因</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="黑名單原因"
                      value={searchFilters.blacklistReason}
                      onChange={(e) => updateSearchFilter('blacklistReason', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* 第二行 - 車輛詳細資訊 */}
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>車輛品牌</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="車輛品牌"
                      value={searchFilters.brand}
                      onChange={(e) => updateSearchFilter('brand', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>車輛顏色</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="車輛顏色"
                      value={searchFilters.color}
                      onChange={(e) => updateSearchFilter('color', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>車輛類型</Form.Label>
                    <Form.Select
                      value={searchFilters.vehicleType}
                      onChange={(e) => updateSearchFilter('vehicleType', e.target.value)}
                    >
                      <option value="all">全部類型</option>
                      <option value="car">汽車</option>
                      <option value="motorcycle">機車</option>
                      <option value="truck">卡車</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>狀態</Form.Label>
                    <Form.Select
                      value={searchFilters.status}
                      onChange={(e) => updateSearchFilter('status', e.target.value)}
                    >
                      <option value="all">全部狀態</option>
                      <option value="blacklisted">生效中</option>
                      <option value="inactive">已停用</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {/* 第三行 - 日期範圍篩選 */}
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>加入黑名單日期範圍</Form.Label>
                    <Row>
                      <Col>
                        <Form.Control
                          type="date"
                          value={searchFilters.blacklistDateFrom}
                          onChange={(e) => updateSearchFilter('blacklistDateFrom', e.target.value)}
                        />
                      </Col>
                      <Col xs="auto" className="d-flex align-items-center">
                        <span className="text-muted">至</span>
                      </Col>
                      <Col>
                        <Form.Control
                          type="date"
                          value={searchFilters.blacklistDateTo}
                          onChange={(e) => updateSearchFilter('blacklistDateTo', e.target.value)}
                        />
                      </Col>
                    </Row>
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
        </Card.Body>
      </Card>

      {/* 黑名單車輛表格 */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">黑名單車輛列表 ({filteredVehicles.length} 輛車輛)</h5>
        </Card.Header>
        <Card.Body className="p-0">
          {currentVehicles.length === 0 ? (
            <div className="text-center py-4">
              <i className="fas fa-ban fa-3x text-muted mb-3"></i>
              <p className="text-muted">沒有找到符合條件的黑名單車輛</p>
            </div>
          ) : (
            <Table responsive striped hover className="mb-0">
              <thead className="table-dark">
                <tr>
                  <th>車牌號碼</th>
                  <th>車主姓名</th>
                  <th>聯絡電話</th>
                  <th>車輛類型</th>
                  <th>品牌型號</th>
                  <th>顏色</th>
                  <th>黑名單原因</th>
                  <th>加入日期</th>
                  <th>加入者</th>
                  <th>違規次數</th>
                  <th>狀態</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {currentVehicles.map((vehicle) => (
                  <tr key={vehicle.id}>
                    <td className="fw-bold text-danger">{vehicle.plateNumber}</td>
                    <td>{vehicle.ownerName}</td>
                    <td>{vehicle.ownerPhone}</td>
                    <td>{getVehicleTypeText(vehicle.vehicleType)}</td>
                    <td>{vehicle.brand} {vehicle.model}</td>
                    <td>{vehicle.color}</td>
                    <td>{vehicle.blacklistReason || '未提供'}</td>
                    <td>{vehicle.blacklistDate || vehicle.registrationDate}</td>
                    <td>{vehicle.blacklistBy || '系統管理員'}</td>
                    <td>
                      <Badge bg="warning">{vehicle.totalViolations || 0}</Badge>
                    </td>
                    <td>
                      <Badge bg={getStatusColor(vehicle.status)}>
                        {getStatusText(vehicle.status)}
                      </Badge>
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEditBlacklistVehicle(vehicle)}
                        className="me-1"
                      >
                        <i className="fas fa-edit"></i>
                      </Button>
                      <Button
                        variant={vehicle.status === 'blacklisted' ? "outline-secondary" : "outline-success"}
                        size="sm"
                        onClick={() => handleToggleBlacklistStatus(vehicle.id, vehicle.status)}
                        className="me-1"
                      >
                        <i className={`fas fa-${vehicle.status === 'blacklisted' ? 'pause' : 'play'}`}></i>
                      </Button>
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() => handleRemoveFromBlacklist(vehicle.id)}
                      >
                        <i className="fas fa-check"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* 分頁 */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.First 
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev 
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            />
            
            {[...Array(totalPages)].map((_, index) => {
              const page = index + 1;
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 2 && page <= currentPage + 2)
              ) {
                return (
                  <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Pagination.Item>
                );
              } else if (
                page === currentPage - 3 ||
                page === currentPage + 3
              ) {
                return <Pagination.Ellipsis key={page} />;
              }
              return null;
            })}
            
            <Pagination.Next 
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last 
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}

      {/* 新增/編輯黑名單車輛模態框 */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? '編輯黑名單車輛' : '加入黑名單'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>車牌號碼 *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="請輸入車牌號碼"
                    value={formData.plateNumber || ''}
                    onChange={(e) => setFormData({...formData, plateNumber: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>車主姓名 *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="請輸入車主姓名"
                    value={formData.ownerName || ''}
                    onChange={(e) => setFormData({...formData, ownerName: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>聯絡電話 *</Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="請輸入聯絡電話"
                    value={formData.ownerPhone || ''}
                    onChange={(e) => setFormData({...formData, ownerPhone: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>地址</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="請輸入地址"
                    value={formData.ownerAddress || ''}
                    onChange={(e) => setFormData({...formData, ownerAddress: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>車輛類型 *</Form.Label>
                  <Form.Select
                    value={formData.vehicleType || 'car'}
                    onChange={(e) => setFormData({...formData, vehicleType: e.target.value as any})}
                    required
                  >
                    <option value="car">汽車</option>
                    <option value="motorcycle">機車</option>
                    <option value="truck">卡車</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>品牌</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="請輸入品牌"
                    value={formData.brand || ''}
                    onChange={(e) => setFormData({...formData, brand: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>型號</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="請輸入型號"
                    value={formData.model || ''}
                    onChange={(e) => setFormData({...formData, model: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>顏色</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="請輸入顏色"
                    value={formData.color || ''}
                    onChange={(e) => setFormData({...formData, color: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>黑名單原因 *</Form.Label>
                  <Form.Select
                    value={formData.blacklistReason || ''}
                    onChange={(e) => setFormData({...formData, blacklistReason: e.target.value})}
                    required
                  >
                    <option value="">請選擇黑名單原因</option>
                    <option value="違規停車">違規停車</option>
                    <option value="惡意欠費">惡意欠費</option>
                    <option value="破壞設施">破壞設施</option>
                    <option value="妨礙他人">妨礙他人</option>
                    <option value="偽造證件">偽造證件</option>
                    <option value="其他違規行為">其他違規行為</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>備註</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="請輸入詳細說明或備註"
                    value={formData.notes || ''}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            取消
          </Button>
          <Button 
            variant="danger" 
            onClick={handleSaveBlacklistVehicle}
            disabled={!formData.plateNumber || !formData.ownerName || !formData.ownerPhone || !formData.blacklistReason}
          >
            {isEditing ? '更新' : '加入黑名單'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BlacklistManagement;